import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { generateWhatsAppReport } from '../services/whatsappReportService';


function DailyAttendanceView({ selectedDate, userClass, onClose }) {
  const [students, setStudents] = useState([]);
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDiaryView, setShowDiaryView] = useState(false);
  const [homeworkStatus, setHomeworkStatus] = useState({}); // { studentId: { isDone: boolean, pendingSubjects: [] } }

  const subjects = ['Maths', 'English', 'Science', 'Hindi'];

  useEffect(() => {
    fetchStudentsAndAttendance();
  }, [selectedDate, userClass]);

  const fetchStudentsAndAttendance = async () => {
    try {
      setLoading(true);
      
      // Fetch students in the class
      const studentsResponse = await axios.get(`/api/users?role=student&class=${userClass}`);
      const classStudents = studentsResponse.data;
      
      // Fetch attendance records for the selected date
      const attendanceResponse = await axios.get(`/api/attendance-records/${userClass}`);
      const dayAttendance = attendanceResponse.data.filter(record => record.date === selectedDate);
      
      setStudents(classStudents);
      setAttendanceRecords(dayAttendance);

      // Initialize homework status for all students
      const initialHomeworkStatus = {};
      classStudents.forEach(student => {
        const record = dayAttendance.find(r => r.studentId === student._id);
        if (record && record.status === 'present') {
          initialHomeworkStatus[student._id] = {
            isDone: record.homework?.isDone ?? true, // Default to true if not specified
            pendingSubjects: record.homework?.pendingSubjects ?? []
          };
        } else {
          initialHomeworkStatus[student._id] = {
            isDone: true,
            pendingSubjects: []
          };
        }
      });
      setHomeworkStatus(initialHomeworkStatus);

    } catch (error) {
      console.error('Error fetching attendance data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStudentStatus = (studentId) => {
    const record = attendanceRecords.find(r => r.studentId === studentId);
    return record ? record.status : 'not_marked';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'present': return '#4caf50';
      case 'absent': return '#f44336';
      case 'late': return '#ff9800';
      default: return '#9e9e9e';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'present': return '✅';
      case 'absent': return '❌';
      case 'late': return '⏰';
      default: return '❓';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'present': return 'Present';
      case 'absent': return 'Absent';
      case 'late': return 'Present (Late)';
      default: return 'Not Marked';
    }
  };

  const toggleHomeworkDone = (studentId) => {
    setHomeworkStatus(prev => ({
      ...prev,
      [studentId]: {
        isDone: !prev[studentId].isDone,
        pendingSubjects: prev[studentId].isDone ? [] : prev[studentId].pendingSubjects // Clear subjects if marked done
      }
    }));
  };

  const togglePendingSubject = (studentId, subject) => {
    setHomeworkStatus(prev => {
      const currentSubjects = prev[studentId].pendingSubjects;
      const updatedSubjects = currentSubjects.includes(subject)
        ? currentSubjects.filter(s => s !== subject)
        : [...currentSubjects, subject];
      return {
        ...prev,
        [studentId]: {
          ...prev[studentId],
          pendingSubjects: updatedSubjects
        }
      };
    });
  };

  const toggleAllSubjects = (studentId) => {
    setHomeworkStatus(prev => {
      const allSubjectsPending = prev[studentId].pendingSubjects.length === subjects.length;
      return {
        ...prev,
        [studentId]: {
          ...prev[studentId],
          pendingSubjects: allSubjectsPending ? [] : [...subjects]
        }
      };
    });
  };


  const markAttendance = async (studentId, status) => {
    try {
      const homework = homeworkStatus[studentId];
      await axios.post('/api/attendance-records', {
        studentId,
        date: selectedDate,
        status,
        class: userClass,
        markedBy: 'Teacher', // Required field for the API
        homework: status === 'present' ? homework : undefined // Only send homework if student is present
      });
      
      // Refresh attendance data
      fetchStudentsAndAttendance();
    } catch (error) {
      console.error('Error marking attendance:', error);
      alert('Failed to mark attendance. Please try again.');
    }
  };

  const presentCount = attendanceRecords.filter(r => r.status === 'present').length;
  const absentCount = attendanceRecords.filter(r => r.status === 'absent').length;
  const lateCount = attendanceRecords.filter(r => r.status === 'late').length;
  const notMarkedCount = students.length - attendanceRecords.length;

  const handleGenerateAndShareReport = async () => {
    setLoading(true);
    try {
      // Re-fetch students and attendance to ensure the latest data for the report
      const studentsResponse = await axios.get(`/api/users?role=student&class=${userClass}`);
      const classStudents = studentsResponse.data;
      const attendanceResponse = await axios.get(`/api/attendance-records/${userClass}`);
      const dayAttendance = attendanceResponse.data.filter(record => record.date === selectedDate);

      const studentsForReport = classStudents.map(student => {
        const record = dayAttendance.find(r => r.studentId === student._id);
        const status = record ? record.status : 'not_marked';
        const homework = record ? record.homework : { isDone: true, pendingSubjects: [] };
        return { ...student, status, homework };
      });

      const reportMessage = generateWhatsAppReport(selectedDate, userClass, studentsForReport);

      if (navigator.share) {
        await navigator.share({
          title: 'Daily Classroom & Homework Digest',
          text: reportMessage,
        });
      } else {
        await navigator.clipboard.writeText(reportMessage);
        alert('Daily report copied to clipboard! Paste directly in WhatsApp.');
      }
    } catch (error) {
      console.error('Error generating or sharing report:', error);
      alert('Failed to generate or share report.');
    } finally {
      setLoading(false);
    }
  };

  // Sort students by status priority: absent > late > not_marked > present
  const sortedStudents = [...students].sort((a, b) => {
    const statusA = getStudentStatus(a._id);
    const statusB = getStudentStatus(b._id);
    const priority = { 'absent': 0, 'late': 1, 'not_marked': 2, 'present': 3 };
    return priority[statusA] - priority[statusB];
  });

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: window.innerWidth <= 768 ? 'flex-start' : 'center',
      justifyContent: 'center',
      zIndex: 1000,
      paddingTop: window.innerWidth <= 768 ? '20px' : '0'
    }}>
      <div style={{
        background: 'white',
        borderRadius: '15px',
        padding: window.innerWidth <= 768 ? '20px' : '25px',
        maxWidth: window.innerWidth <= 768 ? '95%' : '600px',
        width: '100%',
        maxHeight: '90vh',
        overflowY: 'auto',
        boxShadow: '0 10px 30px rgba(0,0,0,0.3)'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px',
          borderBottom: '2px solid #e9ecef',
          paddingBottom: '15px'
        }}>
          <div>
            <h3 style={{ margin: 0, color: '#1e40af', fontSize: window.innerWidth <= 768 ? '18px' : '20px' }}>
              📅 Daily Attendance - {userClass}
            </h3>
            <p style={{ margin: '5px 0 0 0', color: '#6c757d', fontSize: '14px' }}>
              {new Date(selectedDate).toLocaleDateString('en-US', { 
                weekday: 'long', 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </p>
          </div>
          <button
            onClick={handleGenerateAndShareReport}
            style={{
              padding: '8px 15px',
              borderRadius: '8px',
              border: 'none',
              background: '#25d366',
              color: 'white',
              fontWeight: 'bold',
              fontSize: window.innerWidth <= 480 ? '12px' : '14px',
              cursor: 'pointer',
              minHeight: '36px',
              display: 'flex',
              alignItems: 'center',
              gap: '5px'
            }}
          >
            <i className="fab fa-whatsapp"></i> Share Report
          </button>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#6c757d'
            }}
          >
            ×
          </button>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <div style={{ fontSize: '24px', marginBottom: '10px' }}>⏳</div>
            <p>Loading attendance data...</p>
          </div>
        ) : (
          <div>
            {/* Summary Stats */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: window.innerWidth <= 480 ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
              gap: '10px',
              marginBottom: '20px',
              padding: '15px',
              background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
              borderRadius: '10px',
              border: '1px solid #dee2e6'
            }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#4caf50' }}>{presentCount}</div>
                <div style={{ fontSize: '12px', color: '#666' }}>Present</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#f44336' }}>{absentCount}</div>
                <div style={{ fontSize: '12px', color: '#666' }}>Absent</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#ff9800' }}>{lateCount}</div>
                <div style={{ fontSize: '12px', color: '#666' }}>Late</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#9e9e9e' }}>{notMarkedCount}</div>
                <div style={{ fontSize: '12px', color: '#666' }}>Not Marked</div>
              </div>
            </div>

            {/* Student List */}
            <div style={{
              maxHeight: window.innerWidth <= 768 ? '400px' : '500px',
              overflowY: 'auto',
              border: '1px solid #e0e0e0',
              borderRadius: '8px',
              background: '#fafafa'
            }}>
              {sortedStudents.length > 0 ? (
                sortedStudents.map((student, index) => {
                  const status = getStudentStatus(student._id);
                  return (
                    <div key={student._id} style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: window.innerWidth <= 768 ? '12px' : '15px',
                      background: 'white',
                      borderBottom: index < sortedStudents.length - 1 ? '1px solid #e0e0e0' : 'none',
                      borderRadius: index === 0 ? '8px 8px 0 0' : index === sortedStudents.length - 1 ? '0 0 8px 8px' : '0'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                        <div style={{
                          width: window.innerWidth <= 768 ? '40px' : '45px',
                          height: window.innerWidth <= 768 ? '40px' : '45px',
                          borderRadius: '50%',
                          background: student.profilePicture ? `url(${student.profilePicture}) center/cover` : '#f0f0f0',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: window.innerWidth <= 768 ? '16px' : '18px',
                          border: '2px solid #e0e0e0',
                          flexShrink: 0
                        }}>
                          {!student.profilePicture && '👤'}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{
                            fontWeight: 'bold',
                            fontSize: window.innerWidth <= 768 ? '14px' : '15px',
                            color: '#333',
                            marginBottom: '2px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>
                            {student.name}
                          </div>
                          <div style={{
                            fontSize: window.innerWidth <= 768 ? '12px' : '13px',
                            color: '#666',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>
                            {student.studentId && `ID: ${student.studentId}`}
                            {student.section && ` • Section: ${student.section}`}
                          </div>
                        </div>
                      </div>
                      <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '4px',
                        alignItems: 'flex-end'
                      }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          padding: '6px 12px',
                          borderRadius: '20px',
                          background: getStatusColor(status),
                          color: 'white',
                          fontSize: window.innerWidth <= 768 ? '12px' : '13px',
                          fontWeight: 'bold',
                          minWidth: window.innerWidth <= 768 ? '80px' : '90px',
                          justifyContent: 'center'
                        }}>
                          <span>{getStatusIcon(status)}</span>
                          <span>{getStatusText(status)}</span>
                        </div>
                        <div style={{
                          display: 'flex',
                          gap: '4px'
                        }}>
                          <button
                            onClick={() => markAttendance(student._id, 'present')}
                            style={{
                              background: '#4caf50',
                              color: 'white',
                              border: 'none',
                              padding: '4px 8px',
                              borderRadius: '4px',
                              fontSize: '10px',
                              cursor: 'pointer'
                            }}
                          >
                            ✅
                          </button>
                          <button
                            onClick={() => markAttendance(student._id, 'absent')}
                            style={{
                              background: '#f44336',
                              color: 'white',
                              border: 'none',
                              padding: '4px 8px',
                              borderRadius: '4px',
                              fontSize: '10px',
                              cursor: 'pointer'
                            }}
                          >
                            ❌
                          </button>
                          <button
                            onClick={() => markAttendance(student._id, 'late')}
                            style={{
                              background: '#ff9800',
                              color: 'white',
                              border: 'none',
                              padding: '4px 8px',
                              borderRadius: '4px',
                              fontSize: '10px',
                              cursor: 'pointer'
                            }}
                          >
                            ⏰
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div style={{
                  textAlign: 'center',
                  padding: '40px',
                  color: '#666'
                }}>
                  <div style={{ fontSize: '48px', marginBottom: '15px' }}>👥</div>
                  <p style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '5px' }}>No Students Found</p>
                  <p style={{ fontSize: '14px' }}>No students are assigned to this class</p>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div style={{
              marginTop: '20px',
              display: 'flex',
              gap: '10px',
              justifyContent: 'center',
              flexWrap: 'wrap'
            }}>
              <button
                onClick={() => setShowDiaryView(!showDiaryView)}
                style={{
                  background: 'linear-gradient(135deg, #4caf50 0%, #388e3c 100%)',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600'
                }}
              >
                📋 {showDiaryView ? 'Hide' : 'Show'} Daily Attendance
              </button>
              <button
                onClick={onClose}
                style={{
                  background: 'linear-gradient(135deg, #6c757d 0%, #495057 100%)',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600'
                }}
              >
                ✕ Close
              </button>
            </div>

            {/* Linear Daily Attendance View */}
            {showDiaryView && (
              <div style={{
                marginTop: window.innerWidth <= 480 ? '10px' : '20px',
                padding: window.innerWidth <= 480 ? '10px' : '20px',
                background: '#ffffff',
                border: '2px solid #e0e0e0',
                borderRadius: '10px',
                fontFamily: 'Arial, sans-serif',
                fontSize: window.innerWidth <= 480 ? '11px' : '14px'
              }}>
                {/* Header */}
                <div style={{
                  textAlign: 'center',
                  marginBottom: window.innerWidth <= 480 ? '8px' : '20px',
                  borderBottom: '2px solid #333',
                  paddingBottom: window.innerWidth <= 480 ? '8px' : '15px'
                }}>
                  <h2 style={{
                    margin: 0,
                    color: '#333',
                    fontSize: window.innerWidth <= 480 ? '14px' : '22px',
                    fontWeight: 'bold'
                  }}>📋 DAILY ATTENDANCE REPORT</h2>
                  <p style={{
                    margin: window.innerWidth <= 480 ? '4px 0 0 0' : '8px 0 0 0',
                    color: '#666',
                    fontSize: window.innerWidth <= 480 ? '10px' : '16px',
                    fontWeight: '500'
                  }}>
                    Class: {userClass} | Date: {new Date(selectedDate).toLocaleDateString('en-US', { 
                      weekday: 'long', 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                  </p>
                </div>

                {/* Summary Statistics */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-around',
                  marginBottom: window.innerWidth <= 480 ? '8px' : '25px',
                  padding: window.innerWidth <= 480 ? '6px' : '15px',
                  background: '#f8f9fa',
                  border: '1px solid #dee2e6',
                  borderRadius: '5px'
                }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: window.innerWidth <= 480 ? '12px' : '20px', fontWeight: 'bold', color: '#28a745' }}>✅ {presentCount}</div>
                    <div style={{ fontSize: window.innerWidth <= 480 ? '8px' : '14px', color: '#666' }}>Present</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: window.innerWidth <= 480 ? '12px' : '20px', fontWeight: 'bold', color: '#dc3545' }}>❌ {absentCount}</div>
                    <div style={{ fontSize: window.innerWidth <= 480 ? '8px' : '14px', color: '#666' }}>Absent</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: window.innerWidth <= 480 ? '12px' : '20px', fontWeight: 'bold', color: '#ffc107' }}>⏰ {lateCount}</div>
                    <div style={{ fontSize: window.innerWidth <= 480 ? '8px' : '14px', color: '#666' }}>Late</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: window.innerWidth <= 480 ? '12px' : '20px', fontWeight: 'bold', color: '#6c757d' }}>❓ {notMarkedCount}</div>
                    <div style={{ fontSize: window.innerWidth <= 480 ? '8px' : '14px', color: '#666' }}>Not Marked</div>
                  </div>
                </div>

                {/* Student List Header */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: window.innerWidth <= 480 ? '1fr 60px' : '1fr 120px 100px',
                  gap: window.innerWidth <= 480 ? '5px' : '10px',
                  padding: window.innerWidth <= 480 ? '6px' : '10px',
                  background: '#343a40',
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: window.innerWidth <= 480 ? '10px' : '14px',
                  borderRadius: '5px 5px 0 0'
                }}>
                  <div>STUDENT NAME</div>
                  {window.innerWidth > 480 && <div>STUDENT ID</div>}
                  <div>STATUS</div>
                </div>

                {/* Student Records */}
                <div style={{
                  border: '1px solid #dee2e6',
                  borderTop: 'none',
                  borderRadius: '0 0 5px 5px'
                }}>
                  {sortedStudents.map((student, index) => {
                    const status = getStudentStatus(student._id);
                    return (
                      <div key={student._id} style={{
                        display: 'grid',
                        gridTemplateColumns: window.innerWidth <= 480 ? '1fr 60px' : '1fr 120px 100px',
                        gap: window.innerWidth <= 480 ? '5px' : '10px',
                        padding: window.innerWidth <= 480 ? '4px 6px' : '12px 10px',
                        background: index % 2 === 0 ? '#ffffff' : '#f8f9fa',
                        borderBottom: index < sortedStudents.length - 1 ? '1px solid #dee2e6' : 'none',
                        alignItems: 'center',
                        fontSize: window.innerWidth <= 480 ? '10px' : '14px'
                      }}>
                        <div style={{
                          fontWeight: '500',
                          color: '#333'
                        }}>
                          {student.name}
                        </div>
                        {window.innerWidth > 480 && (
                          <div style={{
                            color: '#666',
                            fontSize: '13px'
                          }}>
                            {student.studentId || 'N/A'}
                          </div>
                        )}
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '5px',
                          fontWeight: 'bold',
                          color: getStatusColor(status),
                          fontSize: window.innerWidth <= 480 ? '9px' : '13px'
                        }}>
                          <span>{getStatusIcon(status)}</span>
                          <span>{getStatusText(status)}</span>
                        </div>
                        {status === 'present' && homeworkStatus[student._id] && (
                          <div style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: '5px',
                            alignItems: 'center',
                            marginLeft: '10px'
                          }}>
                            <button
                              onClick={() => toggleHomeworkDone(student._id)}
                              style={{
                                padding: '8px 12px',
                                borderRadius: '20px',
                                border: 'none',
                                background: homeworkStatus[student._id].isDone ? '#e8f5e9' : '#ffe0b2',
                                color: homeworkStatus[student._id].isDone ? '#2e7d32' : '#fb8c00',
                                fontWeight: 'bold',
                                fontSize: '12px',
                                minWidth: '80px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '5px',
                                minHeight: '36px'
                              }}
                            >
                              HW: {homeworkStatus[student._id].isDone ? '✅ Done' : '❌ Pending'}
                            </button>

                            {!homeworkStatus[student._id].isDone && (
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                                {subjects.map(subject => (
                                  <button
                                    key={subject}
                                    onClick={() => togglePendingSubject(student._id, subject)}
                                    style={{
                                      padding: '8px 12px',
                                      borderRadius: '20px',
                                      border: `1px solid ${homeworkStatus[student._id].pendingSubjects.includes(subject) ? '#e65100' : '#bdbdbd'}`,
                                      background: homeworkStatus[student._id].pendingSubjects.includes(subject) ? '#ffcc80' : '#f5f5f5',
                                      color: homeworkStatus[student._id].pendingSubjects.includes(subject) ? '#b71c1c' : '#424242',
                                      fontWeight: 'bold',
                                      fontSize: '12px',
                                      cursor: 'pointer',
                                      minHeight: '36px'
                                    }}
                                  >
                                    {subject}
                                  </button>
                                ))}
                                <button
                                  onClick={() => toggleAllSubjects(student._id)}
                                  style={{
                                    padding: '8px 12px',
                                    borderRadius: '20px',
                                    border: `1px solid ${homeworkStatus[student._id].pendingSubjects.length === subjects.length ? '#e65100' : '#bdbdbd'}`,
                                    background: homeworkStatus[student._id].pendingSubjects.length === subjects.length ? '#ffcc80' : '#f5f5f5',
                                    color: homeworkStatus[student._id].pendingSubjects.length === subjects.length ? '#b71c1c' : '#424242',
                                    fontWeight: 'bold',
                                    fontSize: '12px',
                                    cursor: 'pointer',
                                    minHeight: '36px'
                                  }}
                                >
                                  {homeworkStatus[student._id].pendingSubjects.length === subjects.length ? 'Clear All' : 'All'}
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                        {status === 'present' && homeworkStatus[student._id] && (
                          <div style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: '5px',
                            alignItems: 'center',
                            marginLeft: '10px'
                          }}>
                            <button
                              onClick={() => toggleHomeworkDone(student._id)}
                              style={{
                                padding: '8px 12px',
                                borderRadius: '20px',
                                border: 'none',
                                background: homeworkStatus[student._id].isDone ? '#e8f5e9' : '#ffe0b2',
                                color: homeworkStatus[student._id].isDone ? '#2e7d32' : '#fb8c00',
                                fontWeight: 'bold',
                                fontSize: '12px',
                                minWidth: '80px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '5px',
                                minHeight: '36px'
                              }}
                            >
                              HW: {homeworkStatus[student._id].isDone ? '✅ Done' : '❌ Pending'}
                            </button>

                            {!homeworkStatus[student._id].isDone && (
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                                {subjects.map(subject => (
                                  <button
                                    key={subject}
                                    onClick={() => togglePendingSubject(student._id, subject)}
                                    style={{
                                      padding: '8px 12px',
                                      borderRadius: '20px',
                                      border: `1px solid ${homeworkStatus[student._id].pendingSubjects.includes(subject) ? '#e65100' : '#bdbdbd'}`,
                                      background: homeworkStatus[student._id].pendingSubjects.includes(subject) ? '#ffcc80' : '#f5f5f5',
                                      color: homeworkStatus[student._id].pendingSubjects.includes(subject) ? '#b71c1c' : '#424242',
                                      fontWeight: 'bold',
                                      fontSize: '12px',
                                      cursor: 'pointer',
                                      minHeight: '36px'
                                    }}
                                  >
                                    {subject}
                                  </button>
                                ))}
                                <button
                                  onClick={() => toggleAllSubjects(student._id)}
                                  style={{
                                    padding: '8px 12px',
                                    borderRadius: '20px',
                                    border: `1px solid ${homeworkStatus[student._id].pendingSubjects.length === subjects.length ? '#e65100' : '#bdbdbd'}`,
                                    background: homeworkStatus[student._id].pendingSubjects.length === subjects.length ? '#ffcc80' : '#f5f5f5',
                                    color: homeworkStatus[student._id].pendingSubjects.length === subjects.length ? '#b71c1c' : '#424242',
                                    fontWeight: 'bold',
                                    fontSize: '12px',
                                    cursor: 'pointer',
                                    minHeight: '36px'
                                  }}
                                >
                                  {homeworkStatus[student._id].pendingSubjects.length === subjects.length ? 'Clear All' : 'All'}
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Footer */}
                <div style={{
                  marginTop: window.innerWidth <= 480 ? '8px' : '20px',
                  padding: window.innerWidth <= 480 ? '6px' : '10px',
                  background: '#f8f9fa',
                  border: '1px solid #dee2e6',
                  borderRadius: '5px',
                  textAlign: 'center',
                  fontSize: window.innerWidth <= 480 ? '8px' : '12px',
                  color: '#666'
                }}>
                  Total: {sortedStudents.length} | Rate: {sortedStudents.length > 0 ? Math.round((presentCount + lateCount) / sortedStudents.length * 100) : 0}%
                  {window.innerWidth > 480 && (
                    <>
                      <br />
                      Generated on: {new Date().toLocaleString()}
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default DailyAttendanceView;